import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@auth-n/authentication.service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { LoggingService } from '@logging/logging.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ExtrasState } from '@utils/route-utils';
import { maskPhoneNumber } from '@utils/phone-utils';
import { ContentBlockComponent } from '@design/layout/content-block/content-block.component';
import { BodyMediumComponent } from '@design/typography/body-md/body-medium.component';
import { PComponent } from '@design/layout/p/p.component';
import { H4Component } from '@design/typography/h4/h4.component';
import { ButtonComponent } from '@design/buttons/button/button.component';
import { InputComponent } from '@design/forms/input/input.component';
import { LostPhoneBannerComponent } from '@auth-n/lost-phone-banner/lost-phone-banner.component';
import { PasswordResetControlsComponent } from '@auth-n/password-reset-controls/password-reset-controls.component';
import { PasswordInputComponent } from '@auth-n/password-input/password-input.component';
import { TestIdDirective } from '@design/core/test-id.directive';
import { AutoFocusDirective } from '@design/forms/app-auto-focus.directive';
import { HelpLinkType, HelpLinksService } from '@app/help-links/help-links.service';

@UntilDestroy()
@Component({
  selector: 'app-sign-in-flow',
  templateUrl: './sign-in-flow.component.html',
  styleUrl: './sign-in-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    ContentBlockComponent,
    BodyMediumComponent,
    PComponent,
    H4Component,
    ButtonComponent,
    InputComponent,
    PasswordInputComponent,
    LostPhoneBannerComponent,
    PasswordResetControlsComponent,
    TestIdDirective,
    AutoFocusDirective
  ]
})
export class SignInFlowComponent implements OnInit {
  private readonly helpLinks = inject(HelpLinksService);

  message: {
    title: string;
    body?: string;
  };

  fetching$ = this.authenticationState.inFlight$;

  // sign-in form
  emailControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  signInFormGroup: UntypedFormGroup = new UntypedFormGroup({
    username: this.emailControl
  });

  // password form
  passwordUsername = new UntypedFormControl('', [Validators.required]);
  passwordControl = new UntypedFormControl('', [Validators.required]);
  passwordSignInGroup: UntypedFormGroup = new UntypedFormGroup({
    username: this.passwordUsername,
    password: this.passwordControl
  });

  // mfa form
  mfaCodeControl = new UntypedFormControl('', [Validators.required]);
  mfaFormGroup: UntypedFormGroup = new UntypedFormGroup({
    mfaCode: this.mfaCodeControl
  });

  // complete password form
  confirmPasswordFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  @ViewChild('passwordResetControls') passwordResetControls: PasswordResetControlsComponent;

  constructor(
    public authenticationState: ActiveAuthenticationStateService,
    private logger: LoggingService,
    private authenticationService: AuthenticationService,
    private router: Router,
    route: ActivatedRoute
  ) {
    this.message = (<ExtrasState>route.snapshot.data).extrasStateResolver?.message;
    this.authenticationState.signInMfaOptions$.pipe(untilDestroyed(this)).subscribe((options) => {
      if (options?.mfaType === 'TOTP_Restart') {
        this.authenticationService
          .userSignIn(this.emailControl.value.toLowerCase(), this.passwordControl.value, 'USER_SRP_AUTH')
          .then(() => {
            this.logger.trace('SignInComponent: restart sign-in non-custom');
          });
      }
    });
  }

  ngOnInit(): void {
    this.authenticationState.signInMfaOptions = undefined;
    this.authenticationState.completePasswordOptions = undefined;
  }

  signIn() {
    this.clearMessage();
    if (!this.signInFormGroup.valid) return;

    this.passwordUsername.setValue(this.emailControl.value);
    this.authenticationService.getSignInJourney(this.emailControl.value.toLowerCase());
  }

  signInPassword() {
    this.clearMessage();
    if (!this.signInFormGroup.valid) return;

    this.authenticationService
      .userSignIn(this.emailControl.value.toLowerCase(), this.passwordControl.value)
      .then(() => this.logger.trace('SignInJourney: signed-in'));
  }

  signInMfa() {
    if (!this.mfaFormGroup.valid) return;

    this.authenticationService.signInMfa(this.mfaCodeControl.value).then(() => this.logger.trace('SignInComponent: signed-in with mfa'));
  }

  completeNewPassword() {
    if (!this.confirmPasswordFormGroup.valid) return;

    this.authenticationService
      .completeNewPassword(this.authenticationState.completePasswordOptions.username, this.passwordResetControls.password)
      .then(() => this.logger.trace('SignInComponent: completed new password'));
  }

  cancel() {
    this.authenticationState.completePasswordOptions = undefined;
    this.authenticationState.signInMfaOptions = undefined;
    this.authenticationState.signInJourney = undefined;
    this.mfaCodeControl.setValue('');
  }

  clearMessage() {
    this.message = null;
  }

  requestResetPassword() {
    this.router.navigate(['/auth', { outlets: { top: ['request-reset-password'] } }]).then();
  }

  goToHelp() {
    window.open(this.helpLinks.getUrl(HelpLinkType.Home), '_blank').focus();
  }

  getMaskedPhoneNumber(phoneNumber: string): string {
    return maskPhoneNumber(phoneNumber);
  }
}
